import { getSavedAuthData } from './axios/interceptor'
import { memoizedRefreshToken } from './axios/refreshToken'

/**
 * `fetch` vers notre API, qui renouvelle le jeton comme le fait axiosClient.
 *
 * Les appels écrits en `fetch` brut portent le jeton à la main et s'arrêtent
 * là : quand il expire, la requête repart en 401 et personne ne le voit. Le
 * biologiste continue de travailler, ses validations ne sont plus
 * enregistrées, et un simple rafraîchissement de page fait disparaître tout
 * ce qu'il croyait avoir posé. C'est arrivé.
 *
 * `axiosClient` ne connaît pas ce problème — son intercepteur rafraîchit et
 * rejoue l'appel. Ce module donne la même chose à qui garde la forme de
 * `fetch`, pour ne pas avoir à réécrire chaque appelant d'un coup : une seule
 * nouvelle tentative, après un renouvellement, et seulement sur 401.
 */
export async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const withAuth = (token: string | null | undefined): RequestInit => ({
    ...init,
    headers: { ...(init.headers ?? {}), Authorization: token || '' },
  })

  const { accessToken } = getSavedAuthData()
  const response = await fetch(url, withAuth(accessToken))
  if (response.status !== 401) return response

  // Une seule reprise : si le renouvellement échoue ou si le second appel
  // repart en 401, la session est bel et bien finie et l'appelant doit le
  // voir plutôt que de boucler.
  const refreshed = await memoizedRefreshToken().catch(() => null)
  if (!refreshed?.accessToken) return response
  return fetch(url, withAuth(refreshed.accessToken))
}
